import React, { useEffect, useState } from 'react';
import {
    Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle,
} from '@mui/material';
import { auth } from '../config/firebase';
import readConnectionId, { clearConnectionId } from '../utils/readShopifyConnectionId';
import completeShopifyConnect from '../utils/completeShopifyConnect';

/*
 * ASK BEFORE ATTACHING A SHOP.
 *
 * A pending Shopify connection used to be attached silently — awaited inside
 * the login path, so a failing one blocked sign-in with no message, and with
 * the id taken from localStorage, whoever signed in next on this browser got
 * the shop attached to their account. Now the signed-in user is shown the shop
 * by name and decides. The key is cleared on success, on refusal, and on a
 * definitive (4xx) failure; a transient failure keeps it for the next visit.
 */
const statusOf = (err) => {
    const m = /HTTP (\d{3})/.exec(String(err?.message || ''));
    return m ? Number(m[1]) : 0;
};

export default function ShopifyConnectPrompt({ onNotify }) {
    const [pending, setPending] = useState(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        const stored = readConnectionId();
        const uid = auth.currentUser?.uid;
        // An id minted for a different account is not this user's to attach.
        if (stored?.value && uid && (!stored.userId || stored.userId === uid)) setPending(stored);
    }, []);

    if (!pending) return null;

    const close = () => setPending(null);
    const decline = () => { clearConnectionId(); close(); };
    const connect = async () => {
        const uid = auth.currentUser?.uid;
        if (!uid) { close(); return; }
        setBusy(true);
        try {
            await completeShopifyConnect(pending.value, uid);
            clearConnectionId();
            onNotify?.(`Connected ${pending.shop}.`, 'success');
        } catch (err) {
            const st = statusOf(err);
            if (st >= 400 && st < 500) clearConnectionId();
            onNotify?.(st >= 400 && st < 500
                ? `Couldn’t connect ${pending.shop} — the link has expired or was already used. Start the connection again from Shopify.`
                : `Couldn’t connect ${pending.shop} right now. We’ll ask again next time.`, 'error');
        } finally {
            setBusy(false);
            close();
        }
    };

    return (
        <Dialog open onClose={busy ? undefined : close} maxWidth="xs" fullWidth aria-labelledby="shopify-connect-title">
            <DialogTitle id="shopify-connect-title" sx={{ fontWeight: 800, fontSize: '1.05rem' }}>
                Connect your Shopify store?
            </DialogTitle>
            <DialogContent>
                <DialogContentText sx={{ fontSize: '0.9rem', lineHeight: 1.6 }}>
                    <strong>{pending.shop}</strong> asked to connect to this account
                    {auth.currentUser?.email ? ` (${auth.currentUser.email})` : ''}. Only connect it if you
                    started this from your own Shopify admin.
                </DialogContentText>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2.5 }}>
                <Button onClick={decline} disabled={busy} sx={{ textTransform: 'none', fontWeight: 700 }}>
                    Don’t connect
                </Button>
                <Button
                    onClick={connect} disabled={busy} variant="contained" disableElevation
                    sx={{ textTransform: 'none', fontWeight: 700 }}
                >
                    {busy ? 'Connecting…' : 'Connect store'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
